import { EuiBasicTable, EuiBasicTableColumn, EuiButton, EuiModal, EuiModalBody, EuiText, EuiTitle } from "@elastic/eui";
import { EuiTableSortingType } from "@elastic/eui/src/components/basic_table/table_types";
import React, { FC, useContext, useEffect, useState } from "react";

import client from "../../api/Client";
import { useEditShopToPrice } from "../../api/react-query/mutations/shop-to-prices";
import API_URL from "../../Constants";
import { extractPrices, useCurrentShopGroup } from "../../utils/PricelistUtils";
import { ShopContext } from "../../utils/Shop";
import { Price, PriceForTable } from "../../utils/types";
import { PriceGrid } from "../PriceGrid";

interface PriceHistoryPickerProps {
    usedPrice: Price;
    close: () => void;
}

export const PriceHistoryPicker: FC<PriceHistoryPickerProps> = ({ usedPrice, close }) => {
    const [pricesForTable, setPricesForTable] = useState<PriceForTable[]>([]);
    const [prices, setPrices] = useState<Price[]>([]);

    const { shopId } = useContext(ShopContext);
    const shopGroup = useCurrentShopGroup();

    const kassaCode = [...usedPrice.internal_product_id.matchAll(/^(\d+)/gm)].map((match) => match[1])[0];
    const { mutate: selectPriceFromHistory } = useEditShopToPrice();

    //TODO: Refactor to React-Query possibly together with Prices.tsx
    useEffect(() => {
        client
            .get(`${API_URL}/v1/prices?skip=0&limit=9999&sort=internal_product_id&filter=shop_group_id=${shopGroup.id}`)
            .then((result) => {
                const prices: Price[] = result.data.filter((price: Price) =>
                    price.internal_product_id.includes(kassaCode)
                );
                setPrices(prices);

                const pricesForTable: PriceForTable[] = [];

                client.get(`/v1/shops-to-prices?limit=0`).then((shopToPrices) => {
                    prices.forEach((price: Price) => {
                        const priceForTable: PriceForTable = {
                            id: price.id,
                            internal_product_id: price.internal_product_id,
                            shop_group_id: price.shop_group_id,
                            created_at: price.created_at,
                            modified_at: price.modified_at,
                            prices: {},
                            model: price["cannabis"] != null ? "New" : "Old",
                            active: shopToPrices.data.find((shopToPrice) => shopToPrice.price_id === price.id)
                                ? "Ja"
                                : "Nee",
                        };

                        extractPrices(price["cannabis"], priceForTable, "cannabis");
                        extractPrices(price["pre_rolled_joints"], priceForTable, "pre_rolled_joints");
                        extractPrices(price["edible"], priceForTable, "edible");

                        //TODO: Sort by average gram price or by date ?
                        pricesForTable.push(priceForTable);
                    });

                    // console.log("pricesForTable", pricesForTable);
                    const sortedPrices = [...pricesForTable].sort((a, b) => {
                        return a.id.localeCompare(b.id);
                    });
                    setPricesForTable(sortedPrices);
                });
            });
    }, [kassaCode, shopGroup.id]);

    const columns: EuiBasicTableColumn<PriceForTable>[] = [
        {
            field: "id",
            name: "ID",
            width: "10%",
            render: (id: string) => <span>{id.substring(0, 8)}</span>,
        },
        {
            field: "internal_product_id",
            name: "Code",
            width: "30%",
            render: (internal_product_id: string) => <EuiText size={"s"}>{internal_product_id}</EuiText>,
        },
        {
            field: "model",
            name: "Prijs Model",
            width: "10%",
            render: (model: string) => <span>{model}</span>,
        },
        {
            field: "prices",
            name: "Prijzen",
            width: "30%",
            render: (prices: Record<string, number>) => (
                <PriceGrid
                    prices={prices}
                    record={undefined}
                    setEditRecord={() => {}}
                    getModalPrices={() => {}}
                    mode={"cannabis"}
                />
            ),
        },
        // Maybe add updated_date field
        // Currently prices don't have modified_at / created_at
        {
            field: "id",
            name: "",
            width: "15%",
            render: (id: string) => <SelectButton id={id} />,
        },
    ];

    const handleSubmit = (shopToPriceId: string) => {
        const selectedPrice = prices.find((price) => price.id === shopToPriceId);
        if (!selectedPrice) {
            console.error("Price not found");
            return;
        }

        const payload = {
            shop_id: shopId,
            new: usedPrice.new,
            price_id: selectedPrice.id,
            product_id: usedPrice.product_id,
            category_id: usedPrice.category_id,
            kind_id: usedPrice.kind_id!,
            active: usedPrice.active,
            deactivated_labels: [],
            action_labels: usedPrice.action_labels,
        };

        selectPriceFromHistory({
            id: usedPrice.id,
            data: payload,
        });
    };

    const SelectButton = ({ id }: { id: string }) => {
        return id !== usedPrice.price_id ? (
            <EuiButton onClick={() => handleSubmit(id)}>Select</EuiButton>
        ) : (
            <EuiButton disabled={true}>Selected </EuiButton>
        );
    };

    const sorting: EuiTableSortingType<PriceForTable> = {
        sort: {
            field: "id",
            direction: "desc",
        },
    };

    return (
        <EuiModal onClose={close} maxWidth={1200}>
            <EuiModalBody>
                <EuiTitle>
                    <h4>Price History Picker</h4>
                </EuiTitle>
                {pricesForTable.length > 0 && (
                    <EuiBasicTable columns={columns} items={pricesForTable} sorting={sorting} />
                )}
            </EuiModalBody>
        </EuiModal>
    );
};
